import { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import MapboxGeoMap from '../charts/MapboxGeoMap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  XMarkIcon,
  ChartBarIcon,
  TableCellsIcon,
  CodeBracketIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  CheckIcon,
  SparklesIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useChartBuilderStore } from '../../store/chartBuilderStore';
import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import { apiHelpers } from '../../services/api';
import { formatCellValue, getPreviewOption } from '../../utils/chartPreviewUtils';
import FilterConfigurator from './FilterConfigurator';
import toast from 'react-hot-toast';

export default function ChartPreview({ dashboardId, onChartCreated, onRefreshRequest }) {
  const {
    chartType,
    chartConfig,
    chartData,
    chartName,
    previewError,
    sqlQuery,
    schema,
    setChartName,
    setPreviewData,
    setPreviewError,
    setChartConfig,
  } = useChartBuilderStore();

  const { addChart } = useDashboardStore();
  const { getCurrentCompanyId } = useAuthStore();
  const { formatCost } = useCreditCosts();

  const [showSQL, setShowSQL] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [filterConfig, setFilterConfig] = useState([]);
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [hideLegend, setHideLegend] = useState(false);
  const [geoCluster, setGeoCluster] = useState(true);
  const [geoTimeEnabled, setGeoTimeEnabled] = useState(false);
  const [geoTimeField, setGeoTimeField] = useState('');
  const [geoTimeStep, setGeoTimeStep] = useState('day');
  const [geoTimeMode, setGeoTimeMode] = useState('trail');
  const [geoSizeField, setGeoSizeField] = useState('');
  const [multiSeriesLayout, setMultiSeriesLayout] = useState(chartConfig?.config?.multiSeriesLayout || 'auto');
  const [sqlExplanation, setSqlExplanation] = useState('');
  const [isExplaining, setIsExplaining] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRunningPreview, setIsRunningPreview] = useState(false);
  const backendOptionRef = useRef(null);

  const queryToRun = chartConfig?.query || sqlQuery;
  const hasQueryButNoData = !!queryToRun && !chartData && !previewError;

  // Auto-fetch backend geocoded option when geo chart has city data but no coordinates
  useEffect(() => {
    if (chartType !== 'geo' || !chartData || chartData.length === 0 || !queryToRun) return;
    const keys = Object.keys(chartData[0] || {});
    const hasCoords = keys.some(k => /(?:^|_|\b)(lat|latitude)(?:$|_|\b)/i.test(k));
    const hasGeoCol = keys.some(k => /(?:^|_|\b)(city|town|municipality|country|nation|state|region|province)(?:$|_|\b)/i.test(k));
    if (!hasCoords && hasGeoCol && !backendOptionRef.current) {
      handleRunPreview(true);
    }
  }, [chartType, chartData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunPreview = async (silent = false) => {
    if (!queryToRun || isRunningPreview) return;
    setIsRunningPreview(true);
    if (!silent) setPreviewError(null);
    try {
      const response = await apiHelpers.previewChartQuery({
        companyId: getCurrentCompanyId(),
        query: queryToRun,
        type: chartType || 'bar',
      });

      setPreviewData(response.data?.data || []);
      backendOptionRef.current = response.data?.echartsOption || null;
      setChartConfig({
        ...chartConfig,
        query: queryToRun,
        config: chartConfig?.config || {},
      });
      if (!silent) toast.success(`Preview loaded (${response.data?.rowCount || 0} rows)`);
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to run preview query';
      if (!silent) {
        setPreviewError(msg);
        toast.error(msg);
      }
    } finally {
      setIsRunningPreview(false);
    }
  };

  const hasAxes = !['pie', 'donut', 'table', 'kpi', 'cohort_table', 'geo', 'funnel'].includes(chartType);

  const previewOption = (() => {
    if (chartType === 'table' || chartType === 'cohort_table' || chartType === 'kpi') return null;
    const previewCfg = chartConfig
      ? { ...chartConfig, config: { ...(chartConfig.config || {}), multiSeriesLayout, geoCluster } }
      : chartConfig;
    const opt = getPreviewOption(chartType, chartData || [], previewCfg);
    if (!opt) {
      if (backendOptionRef.current) return backendOptionRef.current;
      return null;
    }
    if (hasAxes) {
      if (xAxisLabel.trim() && opt.xAxis && !Array.isArray(opt.xAxis)) {
        opt.xAxis = {
          ...opt.xAxis,
          name: xAxisLabel.trim(),
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 },
        };
      }
      if (yAxisLabel.trim() && opt.yAxis && !Array.isArray(opt.yAxis)) {
        opt.yAxis = {
          ...opt.yAxis,
          name: yAxisLabel.trim(),
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: 90,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 },
        };
      }
    }
    if (hideLegend) {
      opt.legend = undefined;
    }
    return opt;
  })();

  const handleExplainSQL = async () => {
    if (!chartConfig?.query) {
      toast.error('No SQL query to explain');
      return;
    }
    setIsExplaining(true);
    setSqlExplanation('');
    try {
      const response = await apiHelpers.explainSQLQuery({
        query: chartConfig.query,
        provider: 'anthropic',
        model: '',
      });
      setSqlExplanation(response.data.explanation);
      const creditsUsed = response.data.creditCost || 3;
      toast.success(`SQL explained (${creditsUsed} ${creditsUsed === 1 ? 'credit' : 'credits'} used)`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to explain SQL query');
    } finally {
      setIsExplaining(false);
    }
  };

  const handleSaveChart = async () => {
    if (!chartConfig || !chartName.trim()) {
      toast.error('Please provide a chart name');
      return;
    }

    const effectiveSourceId =
      useChartBuilderStore.getState().selectedDataSource === 'auto' && useChartBuilderStore.getState().aiSelectedSource
        ? useChartBuilderStore.getState().aiSelectedSource.id
        : useChartBuilderStore.getState().selectedDataSource;
    const effectiveSourceType =
      useChartBuilderStore.getState().selectedDataSource === 'auto' && useChartBuilderStore.getState().aiSelectedSource
        ? useChartBuilderStore.getState().aiSelectedSource.type
        : useChartBuilderStore.getState().selectedSourceType;

    if (!effectiveSourceId || effectiveSourceId === 'auto') {
      toast.error('Please select a data source or wait for AI to select one');
      return;
    }

    setIsSaving(true);
    try {
      let finalChartConfig = { ...(chartConfig.config || chartConfig) };

      if (hasAxes) {
        if (xAxisLabel.trim()) {
          finalChartConfig.xAxis = { ...finalChartConfig.xAxis, label: xAxisLabel.trim() };
        }
        if (yAxisLabel.trim()) {
          finalChartConfig.yAxis = { ...finalChartConfig.yAxis, label: yAxisLabel.trim() };
        }
      }

      if (hideLegend) {
        finalChartConfig.legend = { ...finalChartConfig.legend, show: false };
      }

      finalChartConfig.geoCluster = geoCluster;
      finalChartConfig.geoTimeEnabled = geoTimeEnabled;
      finalChartConfig.geoTimeField = geoTimeField || null;
      finalChartConfig.geoTimeStep = geoTimeStep;
      finalChartConfig.geoTimeMode = geoTimeMode;
      finalChartConfig.geoSizeField = geoSizeField || null;

      if (multiSeriesLayout !== 'auto') {
        finalChartConfig.multiSeriesLayout = multiSeriesLayout;
      }

      if (chartType === 'kpi' && chartData && chartData.length > 0) {
        const valueField =
          finalChartConfig?.kpiConfig?.valueField ||
          finalChartConfig?.series?.[0]?.field ||
          Object.keys(chartData[0])[0];
        finalChartConfig.kpiConfig = {
          ...finalChartConfig.kpiConfig,
          valueField,
        };
      }

      const chartDataPayload = {
        name: chartName.trim(),
        type: chartType,
        dashboardId,
        query: chartConfig.query || sqlQuery,
        chartConfig: finalChartConfig,
        aiGenerated: !!chartConfig.aiGenerated,
        aiPrompt: chartConfig.aiPrompt,
        filterConfig: filterConfig.map(f => ({
          id: f.id,
          column: f.column,
          label: f.label,
          type: f.type,
          columnType: f.columnType,
          enabled: f.enabled,
          defaultValue: f.defaultValue,
          defaultRange: f.defaultRange,
          sourceTable: f.sourceTable,
          sourceColumn: f.sourceColumn,
        })),
      };

      if (effectiveSourceType === 'view' || effectiveSourceType === 'wideview') {
        const views = useChartBuilderStore.getState().wideViews;
        const v = views.find((view) => view._id === effectiveSourceId);
        chartDataPayload.wideViewName = effectiveSourceId.replace('view_', '').replace('wide_', '');
        chartDataPayload.dbtModelName = v?.dbtModelName;
        chartDataPayload.isWideView = true;
      } else if (effectiveSourceType === 'mart') {
        const marts = useChartBuilderStore.getState().marts;
        const m = marts.find((mart) => mart._id === effectiveSourceId);
        chartDataPayload.martId = effectiveSourceId.replace('mart_', '');
        chartDataPayload.dbtModelName = m?.dbtModelName;
        chartDataPayload.isMart = true;
      } else {
        chartDataPayload.dataSourceId = effectiveSourceId;
      }

      const response = await apiHelpers.createChart(chartDataPayload);
      toast.success('Chart created successfully!');
      onChartCreated?.(response.data.chart);
    } catch (error) {
      const errorMessage =
        error.response?.data?.details
          ? `Validation failed: ${error.response.data.details.map((d) => d.message).join(', ')}`
          : error.response?.data?.error || 'Failed to save chart';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 p-3 sm:p-6 flex flex-col min-h-0 overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h3 className="font-medium text-surface-900 dark:text-white">Preview</h3>
          {chartConfig && onRefreshRequest && (
            <button onClick={onRefreshRequest} className="btn-ghost btn-sm">
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 overflow-hidden">
          {!chartConfig && !chartData ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-4">
                <ChartBarIcon className="w-8 h-8 text-surface-400" />
              </div>
              <h4 className="font-medium text-surface-900 dark:text-white mb-2">No chart yet</h4>
              <p className="text-sm text-surface-500 max-w-xs">
                Use the AI Assistant, Visual Builder, or SQL Editor to create a chart. Preview will appear here.
              </p>
            </div>
          ) : hasQueryButNoData ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                <CodeBracketIcon className="w-8 h-8 text-primary-500" />
              </div>
              <h4 className="font-medium text-surface-900 dark:text-white mb-2">Query ready</h4>
              <p className="text-sm text-surface-500 max-w-xs mb-5">
                Run the query to see a live chart preview.
              </p>
              <button
                onClick={handleRunPreview}
                disabled={isRunningPreview}
                className="btn-primary"
              >
                {isRunningPreview ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                    Running...
                  </>
                ) : (
                  <>
                    <ChartBarIcon className="w-5 h-5 mr-2" />
                    Run Preview
                  </>
                )}
              </button>
            </div>
          ) : previewError ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <XMarkIcon className="w-8 h-8 text-red-500" />
              </div>
              <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">Preview Error</h4>
              <p className="text-sm text-surface-500 max-w-xs">{previewError}</p>
            </div>
          ) : chartType === 'table' && chartData && chartData.length > 0 ? (
            <div className="h-full overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                  <tr>
                    {chartData[0] &&
                      Object.keys(chartData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {chartData.slice(0, 50).map((row, i) => (
                    <tr
                      key={i}
                      className="border-b border-surface-100 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-700/50"
                    >
                      {Object.values(row).map((value, j) => (
                        <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300">
                          {formatCellValue(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {chartData.length > 50 && (
                <div className="px-4 py-2 text-center text-xs text-surface-500 bg-surface-50 dark:bg-surface-800 border-t border-surface-200 dark:border-surface-700">
                  Showing 50 of {chartData.length} rows
                </div>
              )}
            </div>
          ) : chartType === 'cohort_table' && chartData && chartData.length > 0 ? (
            <div className="h-full overflow-auto">
              {(() => {
                const keys = Object.keys(chartData[0] || {});
                const cohortField =
                  keys.find(
                    (k) =>
                      k.toLowerCase().includes('cohort') ||
                      (k.toLowerCase().includes('month') && !k.toLowerCase().includes('period'))
                  ) || keys[0];
                const periodField =
                  keys.find(
                    (k) =>
                      k.toLowerCase().includes('period') ||
                      k.toLowerCase().includes('age') ||
                      k.toLowerCase() === 'm'
                  ) || keys[1];
                const valueField =
                  keys.find((k) =>
                    ['value', 'users', 'count', 'retention', 'rate'].some((t) =>
                      k.toLowerCase().includes(t)
                    )
                  ) || keys[2] || keys[1];

                const cohorts = [...new Set(chartData.map((row) => row[cohortField]))].sort();
                const periods = [...new Set(chartData.map((row) => Number(row[periodField])))].sort(
                  (a, b) => a - b
                );

                const pivotMap = {};
                chartData.forEach((row) => {
                  const cohort = row[cohortField];
                  const period = Number(row[periodField]);
                  if (!pivotMap[cohort]) pivotMap[cohort] = {};
                  pivotMap[cohort][period] = row[valueField];
                });

                const getCellStyle = (value, cohort) => {
                  if (value === undefined || value === null) return {};
                  const baseValue = pivotMap[cohort]?.[0] || 1;
                  const intensity = baseValue > 0 ? Math.min(value / baseValue, 1) : 0;
                  return {
                    backgroundColor: `rgba(99, 102, 241, ${intensity * 0.5})`,
                    color: intensity > 0.6 ? 'white' : 'inherit',
                  };
                };

                return (
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-surface-700 dark:text-surface-300 border-b border-r border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700">
                          Cohort
                        </th>
                        {periods.map((period) => (
                          <th
                            key={period}
                            className="px-3 py-2 text-center font-semibold text-surface-700 dark:text-surface-300 border-b border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700 min-w-[50px]"
                          >
                            M{period}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohorts.map((cohort) => (
                        <tr key={cohort} className="border-b border-surface-100 dark:border-surface-700">
                          <td className="px-3 py-2 font-medium text-surface-900 dark:text-white border-r border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 whitespace-nowrap">
                            {formatCellValue(cohort)}
                          </td>
                          {periods.map((period) => {
                            const value = pivotMap[cohort]?.[period];
                            const cellStyle = getCellStyle(value, cohort);
                            return (
                              <td key={period} className="px-3 py-2 text-center" style={cellStyle}>
                                {value !== undefined
                                  ? typeof value === 'number'
                                    ? value.toLocaleString()
                                    : formatCellValue(value)
                                  : '—'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          ) : chartType === 'kpi' && chartData && chartData.length > 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="text-5xl font-display font-bold text-surface-900 dark:text-white mb-2">
                {(() => {
                  const cfg = chartConfig?.config || chartConfig;
                  const valueField =
                    cfg?.kpiConfig?.valueField ||
                    cfg?.series?.[0]?.field ||
                    Object.keys(chartData[0])[0];
                  const value = chartData[0]?.[valueField];
                  const prefix = cfg?.kpiConfig?.prefix || '';
                  const suffix = cfg?.kpiConfig?.suffix || '';
                  const formattedValue =
                    typeof value === 'number' ? new Intl.NumberFormat().format(value) : value;
                  return `${prefix}${formattedValue}${suffix}`;
                })()}
              </div>
              <p className="text-surface-500">
                {(() => {
                  const cfg = chartConfig?.config || chartConfig;
                  return cfg?.kpiConfig?.label || cfg?.name || Object.keys(chartData[0])[0];
                })()}
              </p>
            </div>
          ) : chartType === 'geo' && chartData && chartData.length > 0 ? (
            <div className="h-full p-2">
              <MapboxGeoMap
                data={chartData}
                echartsOption={backendOptionRef.current}
                chartConfig={{ geoCluster, geoTimeEnabled, geoTimeField, geoTimeStep, geoTimeMode, geoSizeField }}
              />
            </div>
          ) : previewOption ? (
            <div className="h-full p-4">
              <ReactECharts
                option={previewOption}
                notMerge={true}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
          ) : chartConfig && chartData ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
                <TableCellsIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="text-sm text-surface-500">Preview not available for this chart type</p>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>

      {chartConfig && (chartConfig.query || sqlQuery) && chartData && (
        <div className="border-t border-surface-200 dark:border-surface-800">
          <button
            onClick={() => setShowSQL(!showSQL)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <CodeBracketIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
              <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                View SQL Query
              </span>
              {showSQL && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                  Advanced
                </span>
              )}
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-surface-400 transition-transform ${showSQL ? 'rotate-180' : ''}`}
            />
          </button>

          {showSQL && (
            <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  SQL Query
                </label>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(chartConfig.query || sqlQuery);
                    toast.success('SQL copied to clipboard!');
                  }}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 transition-colors"
                >
                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
              <div className="bg-surface-900 dark:bg-black rounded-lg p-3 overflow-x-auto">
                <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap">
                  {chartConfig.query || sqlQuery}
                </pre>
              </div>
              <button
                onClick={handleExplainSQL}
                disabled={isExplaining}
                className="btn-ghost btn-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
              >
                {isExplaining ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin mr-1" />
                    Explaining...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4 mr-1" />
                    Explain Query
                    {formatCost('explain_sql_query') && (
                      <span className="ml-1 text-xs opacity-75">{formatCost('explain_sql_query')}</span>
                    )}
                  </>
                )}
              </button>
              {sqlExplanation && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-h-96 overflow-y-auto">
                  <div className="flex items-start gap-2 mb-2">
                    <SparklesIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Query Explanation
                    </h4>
                  </div>
                  <div className="text-sm text-blue-800 dark:text-blue-200 prose prose-sm prose-blue dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{sqlExplanation}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {chartConfig && chartData && (
        <div className="border-t border-surface-200 dark:border-surface-800">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
              <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                Configure Filters
              </span>
              {filterConfig.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                  {filterConfig.filter(f => f.enabled).length}
                </span>
              )}
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-surface-400 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>

          {showFilters && (
            <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 max-h-80 overflow-y-auto">
              <FilterConfigurator
                query={chartConfig.query || sqlQuery}
                schema={schema}
                filterConfig={filterConfig}
                onFilterConfigChange={setFilterConfig}
              />
            </div>
          )}
        </div>
      )}

      {chartConfig && chartData && (hasAxes || chartType === 'geo') && (
        <div className="border-t border-surface-200 dark:border-surface-800">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
              <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                Chart Settings
              </span>
              {(xAxisLabel.trim() || yAxisLabel.trim()) && (
                <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                  Custom labels
                </span>
              )}
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-surface-400 transition-transform ${showSettings ? 'rotate-180' : ''}`}
            />
          </button>

          {showSettings && (
            <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50">
              {hasAxes && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">
                        X-Axis Label
                      </label>
                      <input
                        type="text"
                        value={xAxisLabel}
                        onChange={(e) => setXAxisLabel(e.target.value)}
                        placeholder="Auto-generated if empty"
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">
                        Y-Axis Label
                      </label>
                      <input
                        type="text"
                        value={yAxisLabel}
                        onChange={(e) => setYAxisLabel(e.target.value)}
                        placeholder="Auto-generated if empty"
                        className="input w-full"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-surface-400 mt-2">
                    Leave empty to auto-generate labels from your data fields.
                  </p>
                </>
              )}
              <label className="flex items-center gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={hideLegend}
                  onChange={(e) => setHideLegend(e.target.checked)}
                  className="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs font-medium text-surface-600 dark:text-surface-400">Hide legend (series key)</span>
              </label>
              {chartType === 'geo' && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={geoCluster}
                      onChange={(e) => setGeoCluster(e.target.checked)}
                      className="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs font-medium text-surface-600 dark:text-surface-400">Cluster nearby dots (group by proximity)</span>
                  </label>

                  {/* Dot size field */}
                  {chartData?.length > 0 && (() => {
                    const numericCols = Object.keys(chartData[0] || {}).filter(k => {
                      if (/lat|lng|lon|latitude|longitude/i.test(k) || k.startsWith('_')) return false;
                      return typeof chartData[0][k] === 'number' || !isNaN(Number(chartData[0][k]));
                    });
                    if (numericCols.length === 0) return null;
                    return (
                      <div className="mt-2">
                        <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Dot Size Field</label>
                        <select
                          value={geoSizeField}
                          onChange={(e) => setGeoSizeField(e.target.value)}
                          className="input w-full"
                        >
                          <option value="">Uniform size</option>
                          {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    );
                  })()}

                  {/* Time animation toggle */}
                  <label className="flex items-center gap-2 cursor-pointer mt-3">
                    <input
                      type="checkbox"
                      checked={geoTimeEnabled}
                      onChange={(e) => setGeoTimeEnabled(e.target.checked)}
                      className="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs font-medium text-surface-600 dark:text-surface-400">Enable time animation</span>
                  </label>

                  {geoTimeEnabled && (() => {
                    const allCols = chartData?.length > 0 ? Object.keys(chartData[0] || {}) : [];
                    const dateCols = allCols.filter(k =>
                      /(?:date|time|_at|timestamp|created|updated|occurred)/i.test(k) ||
                      (chartData?.length > 0 && !isNaN(Date.parse(String(chartData[0][k]))))
                    );
                    const dateOptions = dateCols.length > 0 ? dateCols : allCols.filter(k => !k.startsWith('_'));
                    return (
                      <div className="ml-6 mt-1 space-y-2">
                        <div>
                          <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Date Field</label>
                          {dateOptions.length > 0 ? (
                            <select
                              value={geoTimeField}
                              onChange={(e) => setGeoTimeField(e.target.value)}
                              className="input w-full"
                            >
                              <option value="">Select a date field</option>
                              {dateOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={geoTimeField}
                              onChange={(e) => setGeoTimeField(e.target.value)}
                              placeholder="e.g. date, timestamp"
                              className="input w-full"
                            />
                          )}
                          {!geoTimeField && (
                            <p className="text-xs text-amber-500 mt-1">
                              {dateOptions.length === 0 ? 'Run preview to detect columns, or type the field name.' : 'Please select a date field.'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Time Step</label>
                          <select
                            value={geoTimeStep}
                            onChange={(e) => setGeoTimeStep(e.target.value)}
                            className="input w-full"
                          >
                            <option value="hour">Hour</option>
                            <option value="day">Day</option>
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                            <option value="year">Year</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Mode</label>
                          <div className="flex rounded-md overflow-hidden border border-surface-300 dark:border-surface-600">
                            <button
                              type="button"
                              onClick={() => setGeoTimeMode('trail')}
                              className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${geoTimeMode === 'trail' ? 'bg-primary-600 text-white' : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-600'}`}
                            >
                              Trail
                            </button>
                            <button
                              type="button"
                              onClick={() => setGeoTimeMode('snapshot')}
                              className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${geoTimeMode === 'snapshot' ? 'bg-primary-600 text-white' : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-600'}`}
                            >
                              Snapshot
                            </button>
                          </div>
                          <p className="text-xs text-surface-400 mt-1">
                            {geoTimeMode === 'trail' ? 'Accumulates all points up to the current step.' : 'Shows only points within the current step.'}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
              {hasAxes && (
                <div className="mt-3">
                  <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">
                    Multi-Series Layout
                  </label>
                  <select
                    value={multiSeriesLayout}
                    onChange={(e) => setMultiSeriesLayout(e.target.value)}
                    className="input w-full"
                  >
                    <option value="auto">Auto (dual-axis when scales differ)</option>
                    <option value="shared_axis">Shared Axis (grouped bars)</option>
                    <option value="dual_axis">Dual Axis (bar + line combo)</option>
                  </select>
                  <p className="text-xs text-surface-400 mt-1">
                    Controls how multiple numeric series are displayed together.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {chartConfig && chartData && (
        <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <input
              type="text"
              value={chartName}
              onChange={(e) => setChartName(e.target.value)}
              placeholder="Enter chart name..."
              className="input flex-1"
            />
            <button
              onClick={handleSaveChart}
              disabled={isSaving || !chartName.trim()}
              className="btn-primary"
            >
              {isSaving ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5 mr-2" />
                  Save Chart
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
